import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  TwAccordion,
  TwAccordionDetails,
  TwAccordionSummary
} from '../../../components/TwAccordion/TwAccordion';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import TwStop, { TwStopHandle } from '../../../components/TwStop/TwStop';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TwAutocompleteOption } from '../../../components/TwAutocomplete/TwAutocomplete';
import useAuth from '../../../providers/Auth/Auth.provider';
import useTwLocation from '../../../hooks/useTwLocation';
import CreateLocation from '../../Locations/CreateLocation/CreateLocation';
import EditLocation from '../../Locations/EditLocation/EditLocation';
import { LoadLocation } from '../../../redux/api/load/load.model';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import DateUtils from '../../../utils/dateUtils';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { reorderTripStop, updateTripStop } from '../../../redux/features/trip/tripSlice';
import { EntityType } from '../../../redux/Global.model';
import { useLocationTypeOptions } from '../../../hooks/useLocationTypeOptions';

interface TripRouteProps {
  className?: string;
  routeContainerSx?: SxProps;
}

const TripRouteInfo = forwardRef<HTMLDivElement, TripRouteProps>(
  ({ className, routeContainerSx }, ref) => {
    const { data: user } = useAuth();
    const dispatch = useAppDispatch();
    const { addNotification } = useNotifications();
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const use24HourClock =
      (user.userData?.tenantSettings && user.userData.tenantSettings['use-24-hour-clock']) || false;
    const TIMES = !use24HourClock
      ? DateUtils.getDayTimesByInterval(15)
      : DateUtils.getDayTimesByInterval(15, { hourCycle: 'h23' });
    const stopsRef = useRef<Array<TwStopHandle | null>>([]);
    const { locations: twLocations, fetchLocations } = useTwLocation(true);

    // #region Redux selectors
    const isEditTripOpen = useAppSelector((state) => state.navigation.isEditTripOpen);
    const tripData = useAppSelector((state) => state.trip);
    // #endregion

    // #region States
    const [blockFetchLocations, setBlockFetchLocations] = useState<boolean>(false);
    const [hasLocationBeenUpdated, setHasLocationBeenUpdated] = useState<boolean>(false);
    const [times, setTimes] = useState<TwAutocompleteOption[]>([]);
    const [enabled, setEnabled] = useState<boolean>(false);
    const [locations, setLocations] = useLocationTypeOptions(twLocations);
    const [isEditLocationOpen, setIsEditLocationOpen] = useState<boolean>(false);
    const [isCreateLocationOpen, setIsCreateLocationOpen] = useState<boolean>(false);
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    const [activeStopUiId, setActiveStopUiId] = useState<string | null>(null);
    // #endregion

    // #region Functions
    const onDragEnd = (result: DropResult): void => {
      const { destination, source } = result;
      if (!destination) {
        addNotification({
          variant: 'error',
          message: 'Invalid destination: Stops cannot be moved outside its trip.'
        });
        return;
      }

      dispatch(
        reorderTripStop({
          originalIndex: source.index,
          newIndex: destination.index
        })
      );
    };

    const handleEditLocation = (newLocation: LoadLocation, clearDriverPay: boolean): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newLocation?.id || '',
        label: newLocation?.name || '',
        value: newLocation
      };

      if (activeStopUiId) {
        const existingStop = tripData.provider_trip_stops.find(
          (stop) => stop.ui_id === activeStopUiId
        );

        if (existingStop) {
          const newStop = { ...existingStop };
          newStop.location_id = newLocation?.id;
          newStop.provider_location = newAutocompleteOption;

          if (
            newLocation.notes ||
            newLocation.contact_email ||
            newLocation.contact_name ||
            newLocation.contact_number ||
            newLocation.contact_number_ext
          ) {
            newStop.provider_include_location_notes = true;
            newStop.notes = newLocation.notes;
            newStop.contact = {
              contact_name: newLocation.contact_name,
              contact_number: newLocation.contact_number,
              contact_number_ext: newLocation.contact_number_ext,
              contact_email: newLocation.contact_email
            };
          } else {
            newStop.provider_include_location_notes = false;
            newStop.notes = '';
            newStop.contact = undefined;
          }

          dispatch(updateTripStop({ stop: newStop, clearDriverPay }));
          setHasLocationBeenUpdated(true);
        }
      }

      fetchLocations();
      setHasLocationBeenUpdated(false);
    };

    const handleCreateLocation = (newLocation: LoadLocation): void => {
      const newAutocompleteOption: TwAutocompleteOption = {
        id: newLocation.id,
        label: newLocation.name,
        value: newLocation
      };

      setLocations((prevLocations) => [newAutocompleteOption, ...prevLocations]);

      if (activeStopUiId) {
        const existingStop = tripData.provider_trip_stops.find(
          (stop) => stop.ui_id === activeStopUiId
        );

        if (existingStop) {
          const newStop = { ...existingStop };
          newStop.location_id = newLocation?.id;
          newStop.provider_location = newAutocompleteOption;
          newStop.location = newLocation;

          if (
            newLocation.notes ||
            newLocation.contact_email ||
            newLocation.contact_name ||
            newLocation.contact_number ||
            newLocation.contact_number_ext
          ) {
            newStop.provider_include_location_notes = true;
            newStop.notes = newLocation.notes;
            newStop.contact = {
              contact_name: newLocation.contact_name,
              contact_number: newLocation.contact_number,
              contact_number_ext: newLocation.contact_number_ext,
              contact_email: newLocation.contact_email
            };
          } else {
            newStop.provider_include_location_notes = false;
            newStop.notes = '';
            newStop.contact = undefined;
          }

          dispatch(updateTripStop({ stop: newStop, clearDriverPay: true }));
        }
      }

      fetchLocations();
    };

    const handleEditLocationClose = (): void => {
      setSelectedLocationId('');
      setActiveStopUiId(null);
      setIsEditLocationOpen(false);

      const pacContainers = document.getElementsByClassName('pac-container');

      if (pacContainers.length > 0) {
        Array.from(pacContainers).forEach((pc) => pc.remove());
      }
    };

    const handleCreateLocationClose = (): void => {
      setActiveStopUiId(null);
      setIsCreateLocationOpen(false);

      const pacContainers = document.getElementsByClassName('pac-container');

      if (pacContainers.length > 0) {
        Array.from(pacContainers).forEach((pc) => pc.remove());
      }
    };

    const handleOpenCreateLocation = (stopUiId: string): void => {
      setActiveStopUiId(stopUiId);
      setIsCreateLocationOpen(true);
    };

    const handleOpenEditLocation = (locationId: string, stopUiId: string): void => {
      setSelectedLocationId(locationId);
      setActiveStopUiId(stopUiId);
      setIsEditLocationOpen(true);
    };
    const handleStopLocationInputChange = (value: string | null, order: number): void => {
      const hasSelectedValueChanged =
        tripData.provider_trip_stops[order - 1]?.provider_location?.label !== value;
      hasSelectedValueChanged && !blockFetchLocations && fetchLocations(value ?? undefined);
    };
    const handleStopLocationSelected = (): void => {
      fetchLocations();
    };
    // #endregion

    // #region Effects

    /**
     * Use effect to make drag n drop work with StrictMode
     * See link for more info: https://github.com/atlassian/react-beautiful-dnd/issues/2399#issuecomment-1175638194
     */
    useEffect(() => {
      const animation = requestAnimationFrame(() => setEnabled(true));

      return (): void => {
        cancelAnimationFrame(animation);
        setEnabled(false);
      };
    }, []);

    useEffect(() => {
      fetchLocations();

      const timeOptions: TwAutocompleteOption[] = TIMES.map((t) => ({
        id: t,
        label: t,
        value: t
      }));

      setTimes(timeOptions);
    }, []);

    useEffect(() => {
      if (isEditTripOpen) {
        setBlockFetchLocations(true);

        setTimeout(() => {
          setBlockFetchLocations(false);
        }, 500);
      }
    }, [isEditTripOpen]);
    // #endregion

    return (
      <Box
        ref={ref}
        component="div"
        className={`${matchesMobileOrTablet ? 'route-info__main-import' : 'route-info__main'} ${
          className || ''
        }`}
      >
        <span className={matchesMobileOrTablet ? 'route-info__title-left' : 'route-info__title'}>
          Route
        </span>
        <span
          className={matchesMobileOrTablet ? 'route-info__sub-title-left' : 'route-info__sub-title'}
        >
          Add route information
        </span>

        <Box sx={routeContainerSx}>
          <TwAccordion
            defaultExpanded={true}
            key={'trip-route'}
            sx={{ backgroundColor: '#F3F4F6' }}
          >
            <TwAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                flexDirection={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                width={'100%'}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    weight: '500',
                    color: '#6B7280',
                    marginLeft: '10px'
                  }}
                >
                  ROUTE
                </Typography>
              </Stack>
            </TwAccordionSummary>
            <TwAccordionDetails>
              <DragDropContext onDragEnd={onDragEnd}>
                {enabled && (
                  <Droppable droppableId={`droppable-trip-${tripData.ui_id}`} key={tripData.ui_id}>
                    {(droppableProvided, snapshot): JSX.Element => (
                      <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                        {tripData.provider_trip_stops.map((stop, stopIndex) => (
                          <TwStop
                            key={`route-info-stop-${stop.ui_id}`}
                            ref={(element): void => {
                              stopsRef.current[stopIndex] = element;
                            }}
                            stIndex={stopIndex}
                            segmentId={tripData.ui_id}
                            segmentOrder={tripData.order}
                            stopsTotal={tripData.provider_trip_stops.length}
                            stop={stop}
                            locations={locations}
                            times={times}
                            isFullscreenImportLoad={false}
                            isImportLoad={false}
                            onCreateLocationClick={handleOpenCreateLocation}
                            onEditLocationClick={handleOpenEditLocation}
                            onLocationInputChange={handleStopLocationInputChange}
                            onLocationSelected={handleStopLocationSelected}
                            hasLocationBeenUpdated={hasLocationBeenUpdated}
                            isCreateOrder={false}
                            entity={EntityType.TRIP}
                          />
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}
              </DragDropContext>
            </TwAccordionDetails>
          </TwAccordion>
        </Box>

        <CreateLocation
          open={isCreateLocationOpen}
          onClose={handleCreateLocationClose}
          onSuccess={handleCreateLocation}
          entity={EntityType.TRIP}
        />

        <EditLocation
          locationId={selectedLocationId}
          open={isEditLocationOpen}
          onClose={handleEditLocationClose}
          onSuccess={handleEditLocation}
          entity={EntityType.TRIP}
        />
      </Box>
    );
  }
);

TripRouteInfo.displayName = 'TripRoute';
export default TripRouteInfo;
